'use client';

/**
 * The single workspace switcher in the fixed top bar (right cluster, next to
 * the account chip). Unifies what used to be two separate controls
 * (`OrgWorkspaceSwitcher` in the top bar + `ClientSwitcher` in the sidebar,
 * merged 2026-07-06):
 *
 * - **Clients** — the client accounts the caller belongs to. Switching fires
 *   the `setActiveClientAction` server action (client context is server state).
 * - **Organizations** — the team accounts the caller administers
 *   (owner/manager). Each entry just navigates to `/home/{slug}`.
 * - **All organizations** — super admins get a server-backed search on top.
 *
 * On `client-user/` routes the trigger shows the ACTIVE CLIENT name (for both
 * super-admins previewing and members) and the menu offers "Exit to personal",
 * absorbing the two jobs the removed preview banner used to do (BAL-758).
 *
 * Renders nothing when the caller has no clients, no admin orgs, and no
 * super-admin search.
 */
import { useMemo, useState, useTransition } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Check,
  ChevronsUpDown,
  Loader2,
  LogOut,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@kit/ui/command';
import { useDebouncedValue } from '@kit/ui/hooks/use-debounce';
import { Popover, PopoverContent, PopoverTrigger } from '@kit/ui/popover';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { OrganizationAvatar } from '~/components/organization-avatar';
import { superAdminOrgSearchWithLogosAction } from '~/components/organization-search.actions';
import { setActiveClientAction } from '~/lib/user-preferences/actions';
import type { OrgAdminAccount } from './org-admin-section';

interface ClientMembership {
  clientId: string;
  clientName: string;
  accountId?: string;
  role: string;
}

interface WorkspaceSwitcherProps {
  orgAdminAccounts: OrgAdminAccount[];
  clientMemberships: ClientMembership[];
  currentClientId?: string | null;
  /**
   * The current workspace name to show on the trigger (the user's own name in
   * personal context, the client name when acting as a client). Falls back to
   * a neutral "Workspaces" when empty.
   */
  currentLabel?: string;
  /** Super admins get a server-backed "All organizations" search. */
  enableAdminSearch?: boolean;
  /**
   * Called after a successful switch or org navigation — lets a hosting
   * surface (the mobile menu sheet) close itself. Desktop passes nothing.
   */
  onNavigate?: () => void;
  /** Extra classes for the trigger button (mobile renders it full-width). */
  triggerClassName?: string;
}

export function WorkspaceSwitcher({
  orgAdminAccounts,
  clientMemberships,
  currentClientId,
  currentLabel,
  enableAdminSearch = false,
  onNavigate,
  triggerClassName,
}: WorkspaceSwitcherProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const params = useParams<{ account?: string }>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [isPending, startTransition] = useTransition();

  const currentSlug =
    typeof params?.account === 'string' ? params.account : null;

  // Super-admin "all orgs" search (server-side, debounced) — mirrors the
  // former OrgWorkspaceSwitcher.
  const debouncedSearch = useDebouncedValue(search.trim(), 250);
  const orgSearchEnabled = enableAdminSearch && debouncedSearch.length >= 2;
  const orgSearch = useQuery({
    queryKey: ['super-admin-org-search', debouncedSearch],
    enabled: orgSearchEnabled,
    queryFn: () =>
      superAdminOrgSearchWithLogosAction({
        search: debouncedSearch,
        maxResults: 20,
      }),
  });
  const ownSlugs = useMemo(
    () => new Set(orgAdminAccounts.map((org) => org.slug)),
    [orgAdminAccounts],
  );
  const adminResults = (orgSearch.data ?? []).filter(
    (org) => !ownSlugs.has(org.slug),
  );

  const hasClients = clientMemberships.length > 0;
  const hasOrgs = orgAdminAccounts.length > 0;

  if (!hasClients && !hasOrgs && !enableAdminSearch) {
    return null;
  }

  const goToOrg = (slug: string) => {
    setOpen(false);
    onNavigate?.();
    router.push(`/home/${slug}`);
  };

  const switchClient = (clientId: string) => {
    if (clientId === currentClientId) {
      setOpen(false);
      return;
    }

    // Strip a trailing record id so switching never lands on a 404 detail page.
    const basePath = pathname.replace(/\/[0-9a-f-]{36}$/, '');

    startTransition(async () => {
      const result = await setActiveClientAction(clientId);

      if (!result.success) {
        toast.error(t('client:switcher.switchError'), {
          description: result.error,
        });
        return;
      }

      const target = clientMemberships.find((m) => m.clientId === clientId);
      toast.success(
        t('client:switcher.switchSuccess', { clientName: target?.clientName }),
      );
      router.push(basePath);
      router.refresh();
      setOpen(false);
      onNavigate?.();
    });
  };

  // Inside the client portal the active client IS the workspace — surface it on
  // the trigger for BOTH super-admins (whose acting-role is not `client`, so the
  // shell-supplied label would otherwise show their own name) and members. This
  // is why the switcher checkmark and the trigger can't disagree (BAL-758).
  const onClientUserRoute = pathname?.includes('/home/client-user') ?? false;
  const activeClientName = currentClientId
    ? clientMemberships.find((m) => m.clientId === currentClientId)?.clientName
    : undefined;

  const exitToPersonal = () => {
    startTransition(async () => {
      const result = await setActiveClientAction(null);

      if (!result.success) {
        toast.error(t('client:switcher.exitError'), {
          description: result.error,
        });
        return;
      }

      setOpen(false);
      onNavigate?.();
      router.push('/home');
      router.refresh();
    });
  };

  // The current workspace name: the active client when in the portal, else the
  // shell-supplied label (user's own name in personal context), falling back to
  // a neutral label. The current workspace is also checkmarked inside the menu.
  const triggerLabel =
    (onClientUserRoute ? activeClientName : undefined) ||
    currentLabel ||
    t('common:workspaces', { defaultValue: 'Workspaces' });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-test="workspace-switcher-trigger"
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          disabled={isPending}
          className={cn(
            'h-9 shrink-0 justify-between gap-2 rounded-md px-2',
            { 'opacity-70': isPending },
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            <Building2 className="size-4 shrink-0" />
            <span className="max-w-[10rem] truncate text-sm font-medium">
              {triggerLabel}
            </span>
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-test="workspace-switcher-content"
        align="end"
        className="w-[280px] p-0"
      >
        <Command shouldFilter={!enableAdminSearch}>
          <CommandInput
            placeholder={t('organizations:switcher.searchPlaceholder')}
            value={enableAdminSearch ? search : undefined}
            onValueChange={enableAdminSearch ? setSearch : undefined}
          />
          <CommandList>
            {!enableAdminSearch && (
              <CommandEmpty>
                <Trans
                  i18nKey="organizations:switcher.noOrgsFound"
                  defaults="Nothing found."
                />
              </CommandEmpty>
            )}

            {onClientUserRoute && (
              <>
                <CommandGroup>
                  <CommandItem
                    value="exit-to-personal"
                    onSelect={exitToPersonal}
                    data-test="workspace-switcher-exit-personal"
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <LogOut className="text-muted-foreground size-4 shrink-0" />
                    <span className="font-medium">
                      <Trans
                        i18nKey="client:switcher.exitToPersonal"
                        defaults="Exit to personal"
                      />
                    </span>
                  </CommandItem>
                </CommandGroup>
                {(hasClients || hasOrgs) && <CommandSeparator />}
              </>
            )}

            {hasClients && (
              <CommandGroup
                heading={t('client:switcher.groupHeading', {
                  defaultValue: 'Clients',
                })}
              >
                {clientMemberships.map((membership) => (
                  <CommandItem
                    key={membership.clientId}
                    value={`client:${membership.clientName}`}
                    onSelect={() => switchClient(membership.clientId)}
                    data-test="workspace-switcher-client"
                    data-client-id={membership.clientId}
                    data-client-name={membership.clientName}
                    data-active={
                      membership.clientId === currentClientId
                        ? 'true'
                        : undefined
                    }
                    className="flex cursor-pointer items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <Building2 className="text-muted-foreground size-4 shrink-0" />
                      <span className="truncate font-medium">
                        {membership.clientName}
                      </span>
                    </span>
                    {membership.clientId === currentClientId && (
                      <Check className="text-primary size-4 shrink-0" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {hasClients && hasOrgs && <CommandSeparator />}

            {hasOrgs && (
              <CommandGroup
                heading={t('common:routes.organizations', {
                  defaultValue: 'Organizations',
                })}
              >
                {orgAdminAccounts.map((org) => (
                  <CommandItem
                    key={org.slug}
                    value={`org:${org.name}`}
                    onSelect={() => goToOrg(org.slug)}
                    className="flex cursor-pointer items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <OrganizationAvatar
                        name={org.name}
                        logoUrl={org.logoUrl}
                        className="size-6 shrink-0"
                      />
                      <span className="truncate font-medium">{org.name}</span>
                    </span>
                    {org.slug === currentSlug && (
                      <Check className="text-primary size-4 shrink-0" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {enableAdminSearch && (
              <>
                <CommandSeparator />
                <CommandGroup
                  heading={<Trans i18nKey="teams:allOrganizations" />}
                >
                  {debouncedSearch.length < 2 && (
                    <div
                      className="text-muted-foreground px-2 py-1.5 text-xs"
                      data-test="admin-org-search-hint"
                    >
                      <Trans i18nKey="teams:searchOrganizationsHint" />
                    </div>
                  )}
                  {orgSearchEnabled && orgSearch.isLoading && (
                    <div className="text-muted-foreground flex items-center gap-x-2 px-2 py-1.5 text-xs">
                      <Loader2 className="size-3 animate-spin" />
                      <Trans i18nKey="teams:searchingOrganizations" />
                    </div>
                  )}
                  {orgSearchEnabled &&
                    !orgSearch.isLoading &&
                    adminResults.length === 0 && (
                      <div className="text-muted-foreground px-2 py-1.5 text-xs">
                        <Trans i18nKey="teams:noOrganizationsFound" />
                      </div>
                    )}
                  {adminResults.map((org) => (
                    <CommandItem
                      key={org.id}
                      value={`admin-org:${org.slug}`}
                      onSelect={() => goToOrg(org.slug)}
                      className="flex cursor-pointer items-center gap-2"
                      data-test="workspace-switcher-admin-result"
                      data-slug={org.slug}
                    >
                      <OrganizationAvatar
                        name={org.name}
                        logoUrl={org.logoUrl}
                        className="size-6 shrink-0"
                      />
                      <span className="truncate font-medium">{org.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
